import React from 'react';
import {
    CalendarIcon,
    ClockIcon,
    CurrencyPoundIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import {
    getCycleTypeName,
    getNextClassDate,
    getNextPaidDate,
    hasAnyPaidBookings,
    hasAnyUpcomingPendingBookings
} from '../utils/classUtils';

const ClassCard = ({
    venueClass,
    isExpanded,
    onToggleExpansion,
    onBookClass,
    children, // For expanded content
    isPublic = false,
    showUserStatus = true
}) => {
    const nextDate = venueClass.cycleType !== undefined
        ? getNextClassDate(venueClass.startDate, venueClass.cycleType, venueClass.expirationDate)
        : new Date(venueClass.startDate);

    const nextPaidDate = venueClass.cycleType !== undefined && showUserStatus
        ? getNextPaidDate(venueClass.userClasses, venueClass.startDate, venueClass.cycleType)
        : null;

    const isEnrolled = showUserStatus && hasAnyPaidBookings(venueClass.userClasses);
    const hasUpcomingPendingPayments = showUserStatus && hasAnyUpcomingPendingBookings(venueClass.userClasses);

    const handleBookClick = () => {
        if (venueClass.cycleType !== undefined) {
            // For recurring classes, expand to show dates
            onToggleExpansion(venueClass.id);
        } else {
            // For one-time classes, book directly
            onBookClass(venueClass);
        }
    };

    return (
        <div className="card overflow-hidden">
            {/* Class Header */}
            <div className="bg-gray-700 px-6 py-4 border-b border-gray-600">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white mb-2">
                            {venueClass.name}
                        </h3>
                        <p className="text-gray-300 mb-3">
                            {venueClass.description}
                        </p>

                        {/* Class Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="flex items-center text-gray-400">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                <div>
                                    {venueClass.cycleType !== undefined ? (
                                        <>
                                            <div className="text-emerald-400 font-medium">
                                                {getCycleTypeName(venueClass.cycleType)} Class
                                            </div>
                                            {nextPaidDate && showUserStatus ? (
                                                <div className="text-green-400">
                                                    Next Paid: {nextPaidDate.toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        day: 'numeric',
                                                        month: 'short'
                                                    })}
                                                </div>
                                            ) : nextDate ? (
                                                <div>
                                                    Next Available: {nextDate.toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        day: 'numeric',
                                                        month: 'short'
                                                    })}
                                                </div>
                                            ) : null}
                                        </>
                                    ) : (
                                        <div>
                                            {new Date(venueClass.startDate).toLocaleDateString('en-GB', {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'long'
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center text-gray-400">
                                <ClockIcon className="h-4 w-4 mr-2" />
                                <div>
                                    {new Date(venueClass.startDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center text-emerald-400">
                                <CurrencyPoundIcon className="h-4 w-4 mr-2" />
                                <div className="font-semibold">
                                    £{venueClass.price}
                                    {venueClass.cycleType !== undefined && ' per session'}
                                </div>
                            </div>
                        </div>

                        {/* Expiration Warning */}
                        {venueClass.expirationDate && (
                            <div className="mt-3 text-xs text-yellow-400">
                                Class expires: {new Date(venueClass.expirationDate).toLocaleDateString('en-GB', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                })}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col gap-3">
                        <button
                            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                            onClick={handleBookClick}
                        >
                            {isExpanded ? (
                                <>
                                    <ChevronUpIcon className="h-5 w-5 mr-2" />
                                    Hide Dates
                                </>
                            ) : venueClass.cycleType !== undefined ? (
                                <>
                                    <CalendarIcon className="h-5 w-5 mr-2" />
                                    Book Sessions
                                </>
                            ) : (
                                <>
                                    <CalendarIcon className="h-5 w-5 mr-2" />
                                    Book Now
                                </>
                            )}
                        </button>

                        {showUserStatus && isEnrolled && (
                            <div className="flex items-center justify-center px-3 py-2 bg-green-600/20 border border-green-600 text-green-400 rounded text-sm">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Enrolled
                            </div>
                        )}

                        {showUserStatus && hasUpcomingPendingPayments && !isEnrolled && (
                            <div className="flex items-center justify-center px-3 py-2 bg-yellow-600/20 border border-yellow-600 text-yellow-400 rounded text-sm">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                Payment Pending
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && children}
        </div>
    );
};

export default ClassCard;
